import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCircleCheck,
  faPhone,
  faStore,
  faUser,
  faUserPen,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons"
import ModalPortal from "../Modal/ModalPortal"
import Animate, { AnimationTypes } from "../Animate/Animate"
import InputField from "../InputField/InputField"
import Button from "../Button/Button"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import { useSignUpContext } from "../../context/useSignUpContext"
import { useEmployerLogged } from "../../context/useEmployeeContext"
import { formatBrazilianPhone, normalizePhoneDigits } from "../../utils/phoneFormat"
import { INPUT_SIZE } from "../../configuration/InputSize"
import { useCreateOwnerProfile } from "../../hooks/Account/useCreateOwnerProfile"
import { useSaveOwnerProfile } from "../../hooks/Account/useSaveOwnerProfile"

type CompleteOwnerProfilePopupProps = {
  open: boolean
}

export const CompleteOwnerProfilePopup: React.FC<CompleteOwnerProfilePopupProps> = ({ open }) => {
  const { t } = useTranslation("signup")

  const { signUp } = useSignUpContext()

  const { userEmployee } = useEmployerLogged()

  const { handleSaveOwnerProfile } = useSaveOwnerProfile()

  const { name, phoneNumber, companyName, setName, setPhoneNumber, setCompanyName, handleSubmitForm } =
    useCreateOwnerProfile(open, signUp, userEmployee)

  if (!open || !userEmployee) return null

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay} style={{ zIndex: 950 }}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={`${defaultStyle.alert1} min-w-0 overflow-hidden p-0`}>
            <header className="relative overflow-hidden bg-gradient-to-br from-red-400 via-orange-200 to-amber-100 px-5 pb-5 pt-6 text-stone-900">
              <div className="absolute -right-8 -top-8 h-28 w-28 rounded-full bg-white/15 blur-sm" />

              <div className="absolute -bottom-10 -left-8 h-24 w-24 rounded-full bg-red-900/15 blur-sm" />

              <div className="relative flex items-start gap-4">
                <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white text-2xl text-stone-900 shadow-xl shadow-red-900/20">
                  <FontAwesomeIcon icon={faUserPen} />
                </span>

                <div className="min-w-0 flex-1">
                  <div className="mb-1 flex items-center gap-2 text-xs font-black uppercase tracking-wider text-stone-900/90">
                    <FontAwesomeIcon icon={faWandMagicSparkles} />

                    <span>{t("profile_badge")}</span>
                  </div>

                  <h2 className="text-2xl font-black leading-tight">{t("profile_title")}</h2>
                </div>
              </div>
            </header>

            <form
              className="space-y-4 p-4 sm:p-5"
              onSubmit={(event) => {
                event.preventDefault()
                void handleSaveOwnerProfile(handleSubmitForm())
              }}
            >
              <div className="flex items-center gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-bold text-emerald-800">
                <FontAwesomeIcon icon={faCircleCheck} className="text-emerald-600" />

                <span>{t("profile_required_note")}</span>
              </div>

              <InputField
                id="owner-profile-name"
                name="name"
                autoComplete="name"
                type="text"
                icon={faUser}
                value={name}
                maxLength={INPUT_SIZE.PERSON.name}
                onChange={(event) => setName(event.target.value)}
                placeholder={t("profile_name_placeholder")}
                onInvalidMessage={t("input_name_onInvalidMessage")}
                required
                descriptionKey="interactionDescriptions:components.onboarding.completeOwnerProfilePopup.input.profileNamePlaceholder"
              />

              <InputField
                id="owner-profile-phone"
                name="tel"
                autoComplete="tel"
                type="text"
                icon={faPhone}
                inputMode="tel"
                pattern="\([1-9]{2}\) [2-9][0-9]{4}-[0-9]{4}"
                value={formatBrazilianPhone(phoneNumber)}
                maxLength={15}
                onChange={(event) => setPhoneNumber(normalizePhoneDigits(event.target.value))}
                placeholder={t("profile_phone_placeholder")}
                onInvalidMessage={t("input_owner_phone_onInvalidMessage")}
                required
                descriptionKey="interactionDescriptions:components.onboarding.completeOwnerProfilePopup.input.profilePhonePlaceholder"
              />

              <InputField
                id="owner-profile-company"
                name="organization"
                autoComplete="organization"
                type="text"
                icon={faStore}
                value={companyName}
                maxLength={INPUT_SIZE.COMPANY.name}
                onChange={(event) => setCompanyName(event.target.value)}
                placeholder={t("profile_company_placeholder")}
                descriptionKey="interactionDescriptions:components.onboarding.completeOwnerProfilePopup.input.profileCompanyPlaceholder"
              />

              <Button
                label={t("buttons:button_save_and_continue")}
                typeButton="submit"
                type="primary"
                size="medium"
                bgColor="#ef4444"
                onClick={() => {}}
                descriptionKey="interactionDescriptions:components.onboarding.completeOwnerProfilePopup.button.saveAndContinue"
              />
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
